import React, { useState } from 'react';
import './UserAuth.css';

const UserAuth = ({ onLogin, backendUrl, onBack }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');

  // Normalisiere Backend-URL
  const normalizeUrl = (url) => {
    if (!url) return 'http://localhost:8081';
    try {
      const urlObj = new URL(url);
      return `${urlObj.protocol}//${urlObj.host}`;
    } catch {
      return url.replace(/\/zeitece.*$/, '');
    }
  };

  const normalizedBackendUrl = normalizeUrl(backendUrl || localStorage.getItem('backendUrl') || 'http://localhost:8081');

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${normalizedBackendUrl}/api/user/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Anmeldung fehlgeschlagen');
      }

      // Speichere Token und User-Info
      localStorage.setItem('userToken', data.token);
      localStorage.setItem('userInfo', JSON.stringify(data.user));
      
      if (onLogin) {
        onLogin(data.user, data.token);
      }
    } catch (err) {
      setError(err.message || 'Anmeldung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    // Validierung
    if (!email.trim() || !email.includes('@') || !email.includes('.')) {
      setError('Bitte geben Sie eine gültige Email-Adresse ein');
      setLoading(false);
      return;
    }

    if (password.length < 8) {
      setError('Passwort muss mindestens 8 Zeichen lang sein');
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwörter stimmen nicht überein');
      setLoading(false);
      return;
    }

    if (!name.trim()) {
      setError('Name ist erforderlich');
      setLoading(false);
      return;
    }

    // Email-Format Validierung
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Ungültiges Email-Format. Bitte verwenden Sie das Format: name@domain.de');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${normalizedBackendUrl}/api/user/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
          name: name.trim(),
          company: company.trim() || null,
          address: address.trim() || null,
          phone: phone.trim() || null,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Registrierung fehlgeschlagen');
      }

      setSuccess(data.message || 'Registrierung erfolgreich! Sie können sich jetzt anmelden.');
      
      // Wechsle nach 2 Sekunden zum Login
      setTimeout(() => {
        setIsLogin(true);
        setPassword('');
        setConfirmPassword('');
        setSuccess('');
      }, 2000);
    } catch (err) {
      setError(err.message || 'Registrierung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="user-auth-container">
      <div className="user-auth-card">
        {onBack && (
          <button 
            onClick={onBack}
            style={{
              position: 'absolute',
              top: '20px',
              left: '20px',
              background: 'transparent',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              padding: '8px 16px',
              fontSize: '0.9rem',
              cursor: 'pointer',
              color: '#374151'
            }}
            title="Zurück zur Login-Seite"
          >
            ← Zurück
          </button>
        )}
        <h1>ZEITAUF-MOBIL</h1>
        <div className="auth-tabs">
          <button
            className={isLogin ? 'active' : ''}
            onClick={() => {
              setIsLogin(true);
              setError('');
              setSuccess('');
            }}
          >
            Anmelden
          </button>
          <button
            className={!isLogin ? 'active' : ''}
            onClick={() => {
              setIsLogin(false);
              setError('');
              setSuccess('');
            }}
          >
            Registrieren
          </button>
        </div>

        {error && (
          <div className="auth-error">
            {error}
          </div>
        )}

        {success && (
          <div className="auth-success">
            {success}
          </div>
        )}

        {isLogin ? (
          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label htmlFor="login-email">Email *</label>
              <input
                id="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                placeholder="ihre.email@beispiel.de"
              />
            </div>
            <div className="form-group">
              <label htmlFor="login-password">Passwort *</label>
              <input
                id="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                placeholder="Ihr Passwort"
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Wird angemeldet...' : 'Anmelden'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleRegister}>
            <div className="form-group">
              <label htmlFor="register-email">Email *</label>
              <input
                id="register-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                placeholder="ihre.email@beispiel.de"
                pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
              />
              <small style={{ color: '#6b7280', fontSize: '0.85rem', marginTop: '4px', display: 'block' }}>
                Bitte geben Sie eine gültige Email-Adresse ein (z.B. max.mustermann@firma.de)
              </small>
            </div>
            <div className="form-group">
              <label htmlFor="register-name">Name *</label>
              <input
                id="register-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                disabled={loading}
                placeholder="Ihr vollständiger Name"
              />
            </div>
            <div className="form-group">
              <label htmlFor="register-company">Firma</label>
              <input
                id="register-company"
                type="text"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                disabled={loading}
                placeholder="Ihre Firma (optional)"
              />
            </div>
            <div className="form-group">
              <label htmlFor="register-address">Adresse</label>
              <textarea
                id="register-address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                disabled={loading}
                placeholder="Ihre Adresse (optional)"
                rows="3"
              />
            </div>
            <div className="form-group">
              <label htmlFor="register-phone">Telefon</label>
              <input
                id="register-phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                disabled={loading}
                placeholder="+49 123 456789 oder 0123 456789"
                pattern="[\+]?[0-9\s\-\(\)]+"
              />
              <small style={{ color: '#6b7280', fontSize: '0.85rem', marginTop: '4px', display: 'block' }}>
                Format: +49 123 456789 oder 0123 456789 (optional)
              </small>
            </div>
            <div className="form-group">
              <label htmlFor="register-password">Passwort *</label>
              <input
                id="register-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                placeholder="Mindestens 8 Zeichen"
                minLength={8}
              />
            </div>
            <div className="form-group">
              <label htmlFor="register-confirm-password">Passwort bestätigen *</label>
              <input
                id="register-confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={loading}
                placeholder="Passwort wiederholen"
                minLength={8}
              />
            </div>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Wird registriert...' : 'Registrieren'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default UserAuth;


